import { getCurrencySymbol } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ApiService } from '@app/core/services/api/api.service';
import { ShopService } from '@app/services/shops/shop.service';
import { UserService } from '@app/services/user/user.service';
import { PdqToastComponent } from '@app/storybook/components/toast/toast.component';
import { ToastService } from '@app/storybook/services/toast/toast.service';
import { TimeUnits } from '@utils/times-utils';
import { Observable, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { currentPlanOverview, planTemplate } from '../../../../services/settings/settings-dto';
import { SettingsService } from '../../../../services/settings/settings.service';
import { BillingPlanPeriod, BillingTableFields, IPlanField } from './model';
import {
  PaymentMethodChoice,
  PaymentMethodChooserDialogComponent,
} from './payment-method-chooser-dialog/payment-method-chooser-dialog.component';
@Component({
  selector: 'app-billing-plans',
  templateUrl: './billing-plans.component.html',
  styleUrls: ['./billing-plans.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BillingPlansComponent implements OnDestroy, OnInit {
  pendingLoad = 2;
  planTemplates: planTemplate[];
  displayedPlanTemplates: planTemplate[];
  customPlan: planTemplate;
  tableFields: IPlanField[] = BillingTableFields;
  currPlan: any; //BillingPlan object
  currPlanPeriod: BillingPlanPeriod;
  loadPlans$: Observable<any>;
  loadPlansSubsciption$: Subscription;
  getCurrPlan$: any;
  getCurrPlanSubscription$: any;
  shop$: any;
  shop: any;
  isShopify: boolean;
  currencySymbol = '$';
  destroy$ = new Subject();
  // Split billing gating: a payment method must be on file before a Split (Shopify + Stripe) plan can be chosen.
  hasPaymentMethod = false;
  paymentMethodLoaded = false;
  isOpeningPaymentSetup = false;
  commonItemsToDisplay: { text: string; additionalInfo?: string }[] = [
    { text: 'Unlimited users' },
    { text: 'Unlimited SKUs' },
    { text: 'Segmented tests' },
    { text: 'Checkout extensions (Shopify only)' },
    { text: 'Multi-location support' },
    { text: '60-day data history' },
    { text: 'Full customer support', additionalInfo: '7 AM - 7 PM EST chat & email' },
  ];

  constructor(
    private settingsSrv: SettingsService,
    private changeDetector: ChangeDetectorRef,
    private apiService: ApiService,
    private shopService: ShopService,
    public dialog: MatDialog,
    private toastService: ToastService,
    private userService: UserService,
  ) {}

  ngOnInit(): void {
    this.loadPlans();
    this.initShop();
    this.loadPaymentMethodStatus();
  }

  loadPaymentMethodStatus() {
    this.settingsSrv
      .getPlanState()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          const overview = new currentPlanOverview(data);
          this.hasPaymentMethod = !!(overview.creditCardData || overview.achBankAccountData);
          this.paymentMethodLoaded = true;
          this.changeDetector.detectChanges();
        },
        error: () => {
          this.paymentMethodLoaded = true;
          this.changeDetector.detectChanges();
        },
      });
  }

  get shouldShowSplitBillingPaymentMethodBanner(): boolean {
    if (!this.paymentMethodLoaded || this.hasPaymentMethod) return false;
    return !!this.displayedPlanTemplates?.some((p) => p.splitBillingEnabled);
  }

  isPlanBlockedOnPaymentMethod(plan: planTemplate): boolean {
    if (!plan?.splitBillingEnabled) return false;
    return this.paymentMethodLoaded && !this.hasPaymentMethod;
  }

  onAddPaymentMethodFromBanner(): void {
    const dialogRef = this.dialog.open<PaymentMethodChooserDialogComponent, void, PaymentMethodChoice | undefined>(
      PaymentMethodChooserDialogComponent,
      {
        panelClass: 'payment-method-chooser-dialog',
        backdropClass: 'global_modal_overlay',
        autoFocus: false,
      },
    );

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((choice) => {
        if (!choice) return;
        this.openPaymentMethodSetup(choice);
      });
  }

  private async openPaymentMethodSetup(choice: PaymentMethodChoice): Promise<void> {
    if (this.isOpeningPaymentSetup) return;
    if (!this.validateShopBillingData()) return;
    this.isOpeningPaymentSetup = true;
    this.changeDetector.detectChanges();

    try {
      const afterClosed$ =
        choice === 'ach'
          ? await this.settingsSrv.openACHPaymentSetupWindow('create')
          : await this.settingsSrv.openPaymentMethodSetupWindow('create');

      this.isOpeningPaymentSetup = false;
      this.changeDetector.detectChanges();

      afterClosed$.pipe(takeUntil(this.destroy$)).subscribe(() => this.loadPaymentMethodStatus());
    } catch {
      this.isOpeningPaymentSetup = false;
      this.changeDetector.detectChanges();
      const label = choice === 'ach' ? 'ACH' : 'card';
      this.showToast(`Failed to open ${label} setup`, 'danger');
    }
  }

  ngOnDestroy(): void {
    if (this.loadPlansSubsciption$) this.loadPlansSubsciption$.unsubscribe();
    if (this.getCurrPlanSubscription$) this.getCurrPlanSubscription$.unsubscribe();
    this.destroy$.next(true);
  }

  setDefaults() {
    this.currencySymbol = getCurrencySymbol(this.currPlan?.currency, 'narrow') ?? '$';
  }

  adjustSelectedPlan() {
    const selectedPlan = this.displayedPlanTemplates?.find((p) => p.id === this.currPlan?.planTemplate?.id);

    if (selectedPlan?.isPlanPeriodFlexible) {
      const percentageAnnualDiscount = isNaN(+selectedPlan.annualDiscount) ? 0 : +selectedPlan.annualDiscount;
      selectedPlan.period = this.currPlanPeriod;
      selectedPlan.calculatedPrice =
        selectedPlan.period == BillingPlanPeriod.Yearly
          ? Math.floor(selectedPlan.price * 12 * (1 - percentageAnnualDiscount))
          : selectedPlan.price;
      selectedPlan.displayedPeriod = selectedPlan.period == BillingPlanPeriod.Yearly ? 'year' : 'month';
      if (this.isPeriodDynamicPlanTemplate(selectedPlan) && selectedPlan.period === BillingPlanPeriod.Yearly) {
        selectedPlan.deliveriesAmount = (this.amountToNumber(selectedPlan.deliveriesAmount) * 12).toString();
      }
    }
  }

  async initShop() {
    this.shopService.shop$.pipe(takeUntil(this.destroy$)).subscribe((shop) => {
      this.shop = shop;
      if (this.shop) {
        this.isShopify = !this.shop?.platform || this.shop?.platform == 'shopify' ? true : false;
        this.changeDetector.detectChanges();
      }
    });
  }

  async loadPlans() {
    this.loadPlans$ = await this.settingsSrv.getPlanTemplates();
    this.loadPlansSubsciption$ = this.loadPlans$.subscribe((data) => {
      const parsedData = data.map((p) => new planTemplate(p));
      this.customPlan = parsedData.find((p) => p.isCustom);
      this.planTemplates = parsedData.filter((p) => !p.isCustom && p.isActive);
      this.displayedPlanTemplates = this.customPlan
        ? [this.customPlan, ...this.planTemplates]
        : [...this.planTemplates];
      if (this.pendingLoad == 1) {
        this.setDefaults();
        this.adjustSelectedPlan();
      }
      this.pendingLoad -= 1;
      this.changeDetector.detectChanges();
    });

    this.getCurrPlan$ = await this.settingsSrv.getCurrentPlanId();
    this.getCurrPlanSubscription$ = this.getCurrPlan$.subscribe((data) => {
      this.currPlanPeriod = data?.period;
      this.currPlan = data ?? null;
      if (this.pendingLoad == 1) {
        this.setDefaults();
        this.adjustSelectedPlan();
      }
      this.pendingLoad -= 1;
      this.changeDetector.detectChanges();
    });
  }

  onPlanSelected(plan: planTemplate) {
    if (!plan || plan.id === this.currPlan?.planTemplate?.id) return;
    if (this.isPlanBlockedOnPaymentMethod(plan)) {
      this.showToast('Add a payment method before choosing a Split billing plan.', 'danger');
      return;
    }
    plan.isLoading = true;
    this.subscribeToPlan(plan);
  }

  onPlanPeriodChanged(plan: planTemplate, value: boolean) {
    plan.period = value ? BillingPlanPeriod.Yearly : BillingPlanPeriod.Monthly;
    plan.calculatedPrice =
      plan.period == BillingPlanPeriod.Yearly
        ? Math.floor(plan.price * 12 * (1 - (plan.annualDiscount ?? 0)))
        : plan.price;
    plan.displayedPeriod = value ? 'year' : 'month';
    if (plan.deliveriesAmount && this.isPeriodDynamicPlanTemplate(plan)) {
      const currentAmount = this.amountToNumber(plan.deliveriesAmount);
      const deliveriesAmount = value ? currentAmount * 12 : currentAmount / 12;
      plan.deliveriesAmount = planTemplate.separateThousands(deliveriesAmount);
    }
    this.changeDetector.detectChanges();
  }
  private amountToNumber(amount: string) {
    return +amount.replace(/,/g, '');
  }
  getPeriodTxt(plan) {
    if (!plan.price) return 'Billed monthly';
    if (plan.period == BillingPlanPeriod.Monthly) return 'Monthly plan';
    if (plan.period == 'Yearly') return 'Yearly plan';
  }

  isPeriodDynamicPlanTemplate(plan: planTemplate) {
    return plan.periodOptions && plan.periodOptions.length > 1 && plan.periodOptions.includes(BillingPlanPeriod.Yearly);
  }
  subscribeToPlan(plan, isMaster = true) {
    if (!this.isEnabledRegistration(plan)) {
      this.contactUs();
      return;
    }
    this.apiService
      .promissedPost(`billing/plan-choose`, {
        planId: plan.id,
        period: plan.period,
        master: isMaster,
      })
      .then((res) => {
        this.settingsSrv.onPlanSelected$.next(!!res);
        if (res.redirectURL) window.location.href = res.redirectURL;
        else if (res.plan) {
          this.showToast('Registered Successfully', 'success');
          this.currPlan = plan;
          this.setDefaults();
          this.changeDetector.detectChanges();
        }
      })
      .catch((err) => {
        const message = typeof err?.error === 'string' ? err.error : 'Failed to subscribe to plan. Please try again.';
        this.showToast(message, 'danger');
        plan.isLoading = false;
        this.changeDetector.detectChanges();
      });
  }

  isEnabledRegistration(plan) {
    console.log(
      'isEnabledRegistration',
      JSON.stringify(plan),
      this.currPlanPeriod,
      this.isShopify,
      this.userService.isAdmin(),
    );
    return (
      this.currPlanPeriod !== 'Yearly' /*|| this.currPlan?.planTemplate?.periodOptions?.length*/ &&
      (this.isShopify || (this.userService.isAdmin() && plan.isDummy))
    );
  }

  contactUs() {
    const contactUsUrl = 'https://www.prettydamnquick.com/contact-sales';
    window.open(contactUsUrl, '_blank');
  }

  getPlanBtnTxt(plan) {
    if (!this.isEnabledRegistration(plan)) return 'Contact us';

    if (this.currPlan?.planTemplate?.id === plan.id) return 'Selected';
    if (this.currPlan?.period === 'Yearly') return 'Contact us';
    if (!this.currPlan || this.currPlan?.planTemplate?.isTrial || this.currPlan?.planTemplate?.isDummy)
      return 'Get started';
    else return 'Change plan';
  }

  getPlanBtnVariant(plan) {
    return !this.currPlan || plan.price > this.currPlan?.planTemplate?.price || this.currPlan?.planTemplate?.isTrial
      ? 'primary'
      : 'light';
  }

  scroll(el: HTMLElement) {
    el.scrollIntoView();
    this.changeDetector.detectChanges();
  }

  private showToast(message, mode: 'danger' | 'success' | 'info' | 'secondary') {
    this.toastService.show(PdqToastComponent, {
      mode: mode,
      message: message,
      duration: TimeUnits._SECOND_IN_MS * 3,
    });
  }

  private validateShopBillingData(): boolean {
    const shop = this.shopService.currentShopValue;
    if (shop?.metadata?.email) return true;

    this.showToast('Your store is missing required billing information (email). Please fill it in.', 'danger');
    return false;
  }
}
